/**
 * Anticipated earnings — role-aware; the All-players roster shows everyone
 * and scrolls if it ever outgrows the card.
 *
 * The bonus = commission (a % of revenue) + signup bonuses (Clean 365 + VIP).
 * Milestones are a SEPARATE card, never a line here.
 *
 *  - Rep view: their bonus this month + delta, and the "how it adds up"
 *    breakdown (Commission · Clean 365 signups · VIP signups) with a total.
 *  - "All players" (dropdown, always the LAST option — never the default):
 *    roster total, then each live player with their own Q1–Q2 / Q3 (etc.)
 *    breakdown. Team-level "How it adds up" is Team average only.
 *
 * ONE control: the header dropdown, same contract as Milestone bonuses. It
 * opens on the first player; players get their own view with no dropdown.
 *
 * Signup counts aren't wired yet, so those lines read $0 until connected;
 * commission is live from the workspace's revenue KPI.
 *
 * Font weights kept light — 600 only on the headline number, card title and the
 * money values; everything else 500.
 */
import React, { useEffect, useState } from "react";
import SoftBox from "../SoftBox";
import { UseIsAdmin } from "../../hooks/auth";
import { useSelector } from "react-redux";
import { C, money, compact, LightPicker, Avatar, hueFor } from "../SalesBonus/common";
import { programFromConfig, earningsBreakdown, combineEarningsRows, combinedPayBreakdown, hasCombinedPayRules, hasEarningsRules, ladderState, sanitizeCombinedPay, seedCombinedPayFromPrograms } from "../SalesBonus/moneyRules";
import CardHeader, { CARD_ROOT, CARD_BODY_PAD } from "../CardHeader";
import { realReps, isLiveMoneyRep } from "../../helpers/teamAverage";
import { periodWindowLabel } from "../../helpers/periodWindowLabel";

const ALL = "__all";

/* The inset tinted panel + its micro-label. The panel never bleeds to the card's
   edges — it sits inside with 8px of air, which is what makes the two halves read
   as panels rather than as a split background. */
const PANEL = "var(--tvd-tile, #F7F9FC)";
/* Sentence case. These were uppercase + letter-spaced, which reads as a system
   label stamped on the card rather than a heading a person wrote. Weight and
   colour carry the hierarchy instead. */
const KLABEL = {
  fontSize: "11.5px",
  fontWeight: 600,
  letterSpacing: "-0.005em",
  color: "var(--tvd-body, #475467)",
};

/* The three stats (Next milestone · Personal best · Team top) in the two shapes
   the card's scenes need.
     Tile    — the original boxed chip, three across the card's width
     StatRow — a line in the money rail: label left, figure right. Full rail
               width, so it can't truncate the way the chips did ("Next miles…")
               once the customize drawer narrowed the board. */
const Tile = ({ k, v, s }) => (
  <SoftBox sx={{ background: "var(--tvd-tile, #F8FAFC)", border: "1px solid var(--tvd-hair, #EEF1F5)", borderRadius: "10px", padding: "10px 8px", textAlign: "center", minWidth: 0 }}>
    <div style={{ fontSize: "10.5px", fontWeight: 500, color: C.muted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{k}</div>
    <div style={{ fontSize: "17px", fontWeight: 600, color: C.ink, fontVariantNumeric: "tabular-nums", marginTop: "2px" }}>{v}</div>
    <div style={{ fontSize: "10px", color: C.faint, marginTop: "1px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{s}</div>
  </SoftBox>
);

const StatRow = ({ k, v, s }) => (
  <SoftBox sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: "8px", minWidth: 0 }}>
    <span style={{ fontSize: "11.5px", color: C.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={s || undefined}>{k}</span>
    <span style={{ fontSize: "12.5px", fontWeight: 600, color: C.ink, fontVariantNumeric: "tabular-nums", flexShrink: 0 }}>{v}</span>
  </SoftBox>
);

// The rep's CURRENT window total of the card's pay metric — used by "Team top"
// / "Personal best" chips. Label follows the board / card date filter.
const bestOf = (r, amt, periodLabel = "this month") => {
  if (!r) return { amount: 0, label: "" };
  return { amount: amt(r), label: periodLabel };
};

// "2024-02" → "February 2024". The server keys the personal-best month on the
// company clock; anything unparseable just isn't shown.
const monthLabel = (ym) => {
  const m = /^(\d{4})-(\d{2})$/.exec(String(ym || ""));
  if (!m) return "";
  return new Date(Number(m[1]), Number(m[2]) - 1, 1)
    .toLocaleString("en-US", { month: "long", year: "numeric" });
};

/* The card FILLS its cell (so a row never shows a short card floating in a tall
   slot), while the interior stays its natural height inside `data-hug-content`.
   The board measures that interior to decide how tall the row should be — it
   cannot measure the card itself, because the card is the thing being stretched
   to the answer. See resolveHugHeights in DashboardCanvas. */
function Shell({ header = null, bodyPad = null, children }) {
  return (
    <SoftBox sx={{ ...CARD_ROOT, minHeight: "220px" }}>
      <div data-hug-content style={{ flex: "0 0 auto", display: "flex", flexDirection: "column", minHeight: 0 }}>
        {/* The header is flush to the card's edges (house standard), so the side
            padding belongs to the body — not to the card root. */}
        {header}
        <SoftBox sx={{ display: "flex", flexDirection: "column", minHeight: 0, padding: bodyPad || CARD_BODY_PAD }}>
          {children}
        </SoftBox>
      </div>
    </SoftBox>
  );
}

/* The metric's display name. The payload carries the workspace's KPI names so
   a rule can print "Memberships" without the card needing the KPI store. */
const kpiName = (config, id) => ((config && config.kpiNames) || {})[id] || "";

/* Milestone money counts in the window it was BANKED in — a rung crossed in
   April is not this month's pay. The server marks the month each rung was
   cleared (clearedMonths); anything else is treated as earlier. */
const bankedThisMonth = (rep, program) => {
  if (!rep || !rep.clearedMonths) return 0;
  const thisMonth = new Date().toLocaleString("en-US", { month: "long" });
  const { bankedRungs } = ladderState(program, rep.ytd || 0);
  return bankedRungs
    .filter((m) => rep.clearedMonths[m.target] === thisMonth)
    .reduce((sum, m) => sum + m.bonus, 0);
};

/* Homepage still passes periodIdx (0–5). Analytics / scoped cards pass the
   real window id (quarter, ytd, …) so copy matches Match-dashboard / Setup. */
const IDX_TO_WIN = {
  0: "yesterday", 1: "today", 2: "week", 3: "month", 4: "lastWeek", 5: "lastMonth",
};

/* The house card header (56px, flush, hairline divider) — see CardHeader. This
   card used to hand-roll its own tinted strip with negative margins, which is
   how it ended up 3px shorter than the Mountain's and a different colour. */
function Head({ picker, title }) {
  return (
    <CardHeader
      title={title || "Anticipated earnings"}
      /* The window (e.g. "Q3") is intentionally NOT shown here — it is redundant
         with the meta line under the figure ("commission + bonuses Q3") and the
         picker itself. */
      right={picker}
    />
  );
}

/* Nothing configured yet. An admin gets a way in; a player gets a quiet line
   and — critically — no numbers, because an unconfigured card must never look
   like a pay promise. */
function EarningsSetupState({ title, isAdmin, workspaceName }) {
  return (
    <Shell header={<Head picker={null} title={title} />}>
      <SoftBox sx={{ flex: "1 1 auto", minHeight: 0, display: "flex", flexDirection: "column", justifyContent: "center", px: "2px" }}>
        {isAdmin ? (
          <>
            <span style={{ fontSize: "15px", fontWeight: 600, color: C.ink }}>How does this team earn?</span>
            <span style={{ fontSize: "12px", color: C.muted, marginTop: "4px", lineHeight: 1.55 }}>
              No commission or bonuses set up for {workspaceName || "this workspace"} yet — nothing shows to players
              until you do.
            </span>
            <SoftBox sx={{ mt: "14px", display: "flex", flexDirection: "column", gap: "9px" }}>
              {[62, 46, 54].map((w) => (
                <SoftBox key={w} sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <SoftBox sx={{ height: "10px", width: `${w}%`, borderRadius: "5px", background: "var(--tvd-tile, repeating-linear-gradient(45deg,#F1F3F7,#F1F3F7 6px,#F8FAFC 6px,#F8FAFC 10px))" }} />
                  <SoftBox sx={{ height: "10px", width: "44px", borderRadius: "5px", background: "var(--tvd-tile, #F1F3F7)", ml: "auto" }} />
                </SoftBox>
              ))}
            </SoftBox>
            <span style={{ fontSize: "11px", color: C.faint, marginTop: "14px" }}>
              Open this card&rsquo;s settings from Customize to set the rules.
            </span>
          </>
        ) : (
          <span style={{ fontSize: "12.5px", color: C.faint, textAlign: "center" }}>
            Your team&rsquo;s earnings program is being set up.
          </span>
        )}
      </SoftBox>
    </Shell>
  );
}

function Delta({ pct }) {
  if (pct == null || pct <= 0) return null;
  return (
    <span style={{ fontSize: "12px", fontWeight: 600, color: C.green, background: "rgba(18,183,102,.12)", borderRadius: "6px", padding: "2px 7px", marginLeft: "10px" }}>
      +{Math.round(pct)}%
    </span>
  );
}

/* `chip` is the "rows" scene: each rule lifts off the tinted panel as its own
   white row instead of being separated by a hairline. */
const Row = ({ label, sub, value, dim, chip }) => (
  <SoftBox sx={chip
    ? { display: "flex", justifyContent: "space-between", alignItems: "baseline", background: "var(--tvd-card, #FFFFFF)", borderRadius: "8px", padding: "8px 11px", marginTop: "5px", boxShadow: "0 0 0 1px rgba(16,24,40,.04)", "&:first-of-type": { marginTop: 0 } }
    : { display: "flex", justifyContent: "space-between", alignItems: "baseline", padding: "9px 0", borderTop: `1px solid ${C.soft}`, "&:first-of-type": { borderTop: "none", paddingTop: "3px" } }}>
    <span style={{ fontSize: "13px", fontWeight: 500, color: C.ink }}>
      {label}
      {sub ? <span style={{ display: "block", fontSize: "11px", color: C.faint, marginTop: "1px" }}>{sub}</span> : null}
    </span>
    <span style={{ fontSize: "13.5px", fontWeight: 600, color: dim ? C.faint : C.ink, fontVariantNumeric: "tabular-nums" }}>{value}</span>
  </SoftBox>
);

export default function AnticipatedEarnings({
  data, title, hide = {}, labels = {}, scene = "classic",
  period = null, customRange = null, periodIdx = 3, rollupOnly = false,
  /* Multi-ws Analytics: one combined pay plan (metric per workspace). When set,
     "How it adds up" is computed from that — not from concatenated per-ws rules. */
  combinedPay = null,
  bonusPrograms = null,
  breakdownSplit = false,
  breakdownTitle = "",
}) {
  const win = period || IDX_TO_WIN[periodIdx] || "month";
  const periodLabel = periodWindowLabel(win, customRange);
  const connected = !!(data && data.connected);
  // Real logged-in role — false for players AND for an admin who flipped
  // "See the app as → Player". The data payload's own isAdmin is ignored so
  // the Team/As-rep toggle never leaks into a player's view.
  const isAdmin = UseIsAdmin();
  const usersDict = useSelector((s) => s.companyReducer.usersDictionary) || {};
  const repsRaw = (data && data.reps) || [];
  /* Live people only. Missing-from-dict must DROP (archived often absent from
     the company roster payload — keeping unknowns left Wesley on the picker). */
  const peopleReps = realReps(repsRaw).filter((r) => isLiveMoneyRep(usersDict, r));
  const peopleKey = peopleReps.map((r) => String(r.ownerId)).join("|");
  /* Team average is no longer offered anywhere in this card — not as an
     option, not as the default. An admin lands on a blank "Select player"
     picker and must actively choose someone. */
  const reps = peopleReps;
  const totals = (data && data.totals) || {};
  const config = (data && data.config) || {};
  const myId = data && data.myId;
  const pct = Number(config.commissionPct) || 0;

  // THE PAY METRIC. Everything this card prints — the tiles, the per-player
  // amounts, the "% of …" subtitle, the delta pills — follows the metric of
  // the FIRST commission cut in Setup. The ladder's scored-on metric (which
  // the payload still carries as each rep's top-level month/ytd) only drives
  // the "Next milestone" tile; before this, a Commission cut printed EWV
  // numbers everywhere and labeled them "revenue".
  const program = programFromConfig(config);
  const payKpiId = (program.commissions[0] && program.commissions[0].kpiId) || config.revenueKpiId || null;
  const payName = kpiName(config, payKpiId) || "revenue";
  const payAmt = (r, per = "month") => {
    const a = r && r.amounts && payKpiId ? r.amounts[payKpiId] : null;
    if (a && a[per] != null) return Number(a[per]) || 0;
    // Older payloads only aggregate the ladder metric at the top level.
    if (per === "month") return (r && r.month) || 0;
    if (per === "lastMonth") return (r && r.lastMonth) || 0;
    return 0;
  };
  /* Per-player metric label on multi-ws: use that person's primary amount key
     (Bobby → Sold Revenue, Justin → Estimate Won Value) when ids differ. */
  const payNameOf = (r) => {
    if (!r) return payName;
    const bag = r.amounts || {};
    const ids = Object.keys(bag);
    if (ids.length === 1) {
      const n = kpiName(config, ids[0]);
      if (n) return n;
    }
    if (payKpiId && bag[payKpiId] != null) return payName;
    const named = ids.map((id) => kpiName(config, id)).filter(Boolean);
    if (named.length === 1) return named[0];
    if (named.length > 1) return named.join(" · ");
    return payName;
  };
  const payAmtOf = (r, per = "month") => {
    if (!r) return 0;
    if (payKpiId && r.amounts && r.amounts[payKpiId] != null) return payAmt(r, per);
    const bag = r.amounts || {};
    const ids = Object.keys(bag);
    if (ids.length === 1) {
      const a = bag[ids[0]];
      if (a && a[per] != null) return Number(a[per]) || 0;
    }
    return payAmt(r, per);
  };

  // ONE selection: a rep's ownerId, or ALL. Default = blank ("Select player")
  // for an admin — never a preselected person or the team average. Players
  // are pinned to themselves and never see the picker.
  const canPick = isAdmin && connected && peopleReps.length > 0;
  const [sel, setSel] = useState(() => (!isAdmin && myId) ? myId : "");
  useEffect(() => {
    if (!isAdmin) return;
    if (!peopleReps.length) { if (sel !== "") setSel(""); return; }
    const valid = sel === "" || sel === ALL || peopleReps.some((r) => r.ownerId === sel);
    if (!valid) setSel("");
    // peopleKey — roster identity; avoid looping on a fresh array each render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [peopleKey, isAdmin, sel]);
  const activeSel = sel;
  const showAll = canPick && activeSel === ALL;
  const showBlank = canPick && activeSel === "";

  /* No pay rules yet → setup face. Also honor multi-ws combinedPay. Placed
     AFTER every hook so hook order stays identical on every render. */
  if (connected && !hasEarningsRules(program) && !(rollupOnly && combinedPay && hasCombinedPayRules(combinedPay))) {
    return <EarningsSetupState title={title} isAdmin={isAdmin} workspaceName={data && data.workspaceName} />;
  }

  const people = peopleReps.map((r) => ({
    value: r.ownerId,
    label: r.wsName ? `${r.name} (${r.wsName})` : r.name,
  }));
  const playerGroups = (rollupOnly && peopleReps.some((r) => r.wsName))
    ? Object.entries(peopleReps.reduce((acc, r) => {
      const key = r.wsName || "Players";
      (acc[key] || (acc[key] = [])).push({ value: r.ownerId, label: r.name });
      return acc;
    }, {})).map(([label, options]) => ({ label, options }))
    : [{ label: "Players", options: people }];
  const pickerOptions = playerGroups.length === 1
    ? [...playerGroups[0].options, { value: ALL, label: "All players" }]
    : [...playerGroups, { label: "Team", options: [{ value: ALL, label: "All players" }] }];
  const picker = canPick ? (
    <LightPicker
      small
      width="150px"
      options={pickerOptions}
      value={activeSel}
      onChange={setSel}
      placeholder="Select player"
    />
  ) : null;

  // ---- ALL PLAYERS (who's earning this month) ----
  if (showAll) {
    const roster = [...peopleReps].sort((a, b) => (b.commission || 0) - (a.commission || 0));
    const teamCommission = roster.reduce((s, r) => s + (Number(r.commission) || 0), 0);
    const teamMonth = roster.reduce((s, r) => s + payAmtOf(r), 0);
    const teamLast = roster.reduce((s, r) => s + payAmtOf(r, "lastMonth"), 0);
    const teamDelta = teamLast > 0 ? Math.round(((teamMonth - teamLast) / teamLast) * 100) : null;

    /* All players = per-person breakdowns only. Team roll-up "How it adds up"
       stays on Team average — not duplicated above this roster. */
    let effectiveCombinedAll = null;
    if (rollupOnly) {
      if (combinedPay && hasCombinedPayRules(combinedPay)) {
        effectiveCombinedAll = sanitizeCombinedPay(combinedPay);
      } else if (bonusPrograms && typeof bonusPrograms === "object") {
        const seeded = seedCombinedPayFromPrograms(bonusPrograms, Object.keys(bonusPrograms));
        if (hasCombinedPayRules(seeded)) effectiveCombinedAll = seeded;
      }
    }
    const playerBreaks = [];
    roster.forEach((rep) => {
      let broken;
      if (effectiveCombinedAll) {
        broken = combinedPayBreakdown(
          effectiveCombinedAll,
          (rep && rep.amounts) || {},
          (rep && rep.appliesAmounts) || {},
        );
      } else {
        const banked = bankedThisMonth(rep, program);
        const raw = earningsBreakdown({
          program,
          amounts: (rep && rep.amounts) || {},
          kpiLabel: (id) => kpiName(config, id),
          period: "month",
          bankedThisPeriod: banked,
        });
        const { combinedWorkspaceNames } = data || {};
        const across = combinedWorkspaceNames || null;
        broken = {
          rows: rollupOnly
            ? combineEarningsRows(raw.rows, {
              across: across ? `Across ${across}` : null,
              merge: !breakdownSplit,
              title: breakdownTitle || null,
            })
            : raw.rows,
          total: raw.total,
        };
      }
      /* Drop " (avg)" — that tag is for Team average / multi-ws mode copy,
         not a single player's own base. */
      const rows = (broken.rows || []).map((row) => ({
        ...row,
        detail: row.detail ? String(row.detail).replace(/\s*\(avg\)\s*$/i, "") : row.detail,
      }));
      playerBreaks.push({
        rep,
        rows,
        total: Math.round(Number(broken.total) || Number(rep.commission) || 0),
      });
    });
    const rosterTotal = playerBreaks.reduce((s, p) => s + (p.total || 0), 0) || teamCommission;

    return (
      <Shell header={<Head picker={picker} title={title} />}>
        <SoftBox sx={{ flex: "0 0 auto", mt: "6px" }}>
          <span style={{ fontSize: "20px", fontWeight: 600, letterSpacing: "-0.01em", color: C.ink }}>{money(rosterTotal)}</span>
          <Delta pct={teamDelta} />
          <div style={{ fontSize: "12px", fontWeight: 500, color: C.muted, marginTop: "2px" }}>
            Total across {roster.length} player{roster.length === 1 ? "" : "s"} · {periodLabel}
          </div>
        </SoftBox>

        <SoftBox sx={{ flex: "0 0 auto", mt: "12px" }}>
          <div style={{ height: 1, background: C.line, margin: "0 0 8px" }} />
          <div style={{ fontSize: "10.5px", fontWeight: 500, color: C.faint }}>{`Who's earning ${periodLabel}`}</div>
        </SoftBox>

        {/* Cap height so the card's maxH / row neighbor doesn't clip the roster —
           without a max, flex overflow never engages and Vern (etc.) vanish. */}
        <SoftBox
          className="gmbw-earn-all-scroll"
          sx={{
            flex: "1 1 auto",
            minHeight: 0,
            maxHeight: 420,
            mt: "4px",
            overflowY: "auto",
            overflowX: "hidden",
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            pr: "4px",
            mr: "-4px",
            scrollbarGutter: "stable",
          }}
        >
          {roster.length === 0 && (
            <SoftBox sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: C.faint, fontSize: "13px" }}>No revenue tracked yet.</SoftBox>
          )}
          {playerBreaks.map(({ rep: r, rows: pRows, total: pTotal }) => (
            <SoftBox
              key={r.ownerId}
              sx={{
                padding: "10px 0",
                borderTop: `1px solid ${C.soft}`,
                "&:first-of-type": { borderTop: "none", paddingTop: "4px" },
              }}
            >
              <SoftBox sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <Avatar name={r.name} image={r.image} color={hueFor(r.ownerId)} />
                <SoftBox sx={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: "13px", fontWeight: 500, color: C.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.name}</div>
                  <div style={{ fontSize: "11px", fontWeight: 500, color: C.muted }}>{money(payAmtOf(r))} {payNameOf(r)}</div>
                </SoftBox>
                <span style={{ flexShrink: 0, fontSize: "13.5px", fontWeight: 600, color: C.ink, fontVariantNumeric: "tabular-nums" }}>{money(pTotal)}</span>
              </SoftBox>
              <SoftBox
                sx={{
                  mt: "8px",
                  ml: "42px",
                  background: PANEL,
                  borderRadius: "8px",
                  padding: "8px 9px 9px",
                }}
              >
                <div style={{ ...KLABEL, marginBottom: "5px", fontSize: "10.5px" }}>How it adds up</div>
                {pRows.length === 0 && (
                  <div style={{ fontSize: "11.5px", color: C.faint, padding: "2px 0" }}>No pay rules for this window.</div>
                )}
                {pRows.map((row) => (
                  <Row
                    key={row.key}
                    label={row.missing ? `${row.label} — metric removed` : row.label}
                    sub={row.detail}
                    value={row.informational ? "—" : money(row.value)}
                    dim={row.dim || row.missing}
                    chip
                  />
                ))}
              </SoftBox>
            </SoftBox>
          ))}
        </SoftBox>

        <SoftBox sx={{ flex: "0 0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", mt: "10px", pt: "11px", borderTop: `1px solid ${C.line}` }}>
          <span>
            <span style={{ fontSize: "12px", fontWeight: 500, color: C.muted }}>Total </span>
            <span style={{ fontSize: "15px", fontWeight: 600, color: C.ink }}>{money(rosterTotal)}</span>
          </span>
        </SoftBox>
      </Shell>
    );
  }

  // ---- REP view ----
  /* Blank ("Select player") state: render the SAME card shape as a real rep
     — hero figure, tiles, "how it adds up" rows — just fed a zeroed rep, so
     an admin sees the shape of the card (not a plain empty message) until
     they actually pick someone. */
  const rep = showBlank
    ? { ownerId: "", name: "", image: "", amounts: {}, ytd: 0, month: 0, lastMonth: 0, commission: 0, earned: 0, clearedMonths: {} }
    : (peopleReps.find((r) => r.ownerId === activeSel)
      || peopleReps.find((r) => r.ownerId === myId)
      || peopleReps[0]
      || null);

  /* The breakdown is computed from the WORKSPACE'S OWN RULES, not from a
     hardcoded commission + two named signup lines. That hardcoding is what made
     this a sales-only card; now a technicians or door-to-door team composes its
     own rows and the same component serves all of them. */
  const banked = bankedThisMonth(rep, program);
  /* Prefer the multi-ws combined plan (Setup: one rule, metrics per workspace).
     Fall back to legacy per-ws programs + display merge. */
  let effectiveCombined = null;
  if (rollupOnly) {
    if (combinedPay && hasCombinedPayRules(combinedPay)) {
      effectiveCombined = sanitizeCombinedPay(combinedPay);
    } else if (bonusPrograms && typeof bonusPrograms === "object") {
      const seeded = seedCombinedPayFromPrograms(bonusPrograms, Object.keys(bonusPrograms));
      if (hasCombinedPayRules(seeded)) effectiveCombined = seeded;
    }
  }
  let rows;
  let total;
  if (effectiveCombined) {
    const broken = combinedPayBreakdown(
      effectiveCombined,
      (rep && rep.amounts) || {},
      (rep && rep.appliesAmounts) || {},
    );
    rows = broken.rows;
    total = broken.total;
  } else {
    const broken = earningsBreakdown({
      program,
      amounts: (rep && rep.amounts) || {},
      kpiLabel: (id) => kpiName(config, id),
      period: "month",
      bankedThisPeriod: banked,
    });
    const { combinedWorkspaceNames } = data || {};
    const across = combinedWorkspaceNames || null;
    const { rows: rawRows, total: rawTotal } = broken;
    rows = rollupOnly
      ? combineEarningsRows(rawRows, {
        across: across ? `Across ${across}` : null,
        merge: !breakdownSplit,
        title: breakdownTitle || null,
      })
      : rawRows;
    total = rawTotal;
  }
  // The +% pill compares months of the PAY metric, matching every other
  // number on this card (the ladder metric's delta belongs to the milestone
  // card, not here).
  const repLast = payAmt(rep, "lastMonth");
  const repDelta = rep && repLast > 0 ? Math.round(((payAmt(rep) - repLast) / repLast) * 100) : null;

  // The chips row (Next milestone · Personal best · Team top) — only the ones
  // we actually have data for; peers are invisible to players, so Team top
  // renders only when the payload carries the roster. "Next milestone" is the
  // ONE tile that stays on the ladder: rep.ytd is the scored-on metric the
  // Milestone bonuses card climbs. Every chip's label can be renamed per board
  // (Appearance → What to show), so `labels.X` always wins over the default.
  const milestones = [...((programFromConfig(config).milestones || config.milestones || []))].sort((a, b) => a.target - b.target);
  const nr = rep ? milestones.find((m) => rep.ytd < m.target) || null : null;
  // Personal best: the player's highest single MONTH on the pay metric, any
  // year — the server aggregates it beyond the payload's year window. Older
  // payloads without it fall back to the month in progress.
  const bestAll = (rep && rep.amounts && payKpiId && rep.amounts[payKpiId] && rep.amounts[payKpiId].best) || null;
  const best = bestAll && bestAll.amount
    ? { amount: bestAll.amount, label: monthLabel(bestAll.month) }
    : bestOf(rep, payAmt, periodLabel);
  // Admin payloads carry the whole roster — derive the window's leader from it.
  // Player payloads are scoped to the player's own rep, so the server sends a
  // ready-made { name, amount } as data.teamTop instead; the chip shows for
  // both roles either way.
  const rosterTop = reps.length > 1
    ? reps.reduce((top, r) => (bestOf(r, payAmt, periodLabel).amount > bestOf(top, payAmt, periodLabel).amount ? r : top), reps[0])
    : null;
  const serverTop = (data && data.teamTop && data.teamTop.amount) ? data.teamTop : null;
  const teamTop = rosterTop
    ? { name: rosterTop.name, amount: bestOf(rosterTop, payAmt, periodLabel).amount }
    : serverTop;
  // Each chip is optional per board — an admin switches them off in the editor
  // (the field card is slim; In-House keeps all three).
  const Stat = scene === "classic" ? Tile : StatRow;
  const tiles = [];
  if (milestones.length && !hide.milestone) {
    tiles.push(
      <Stat key="next" k={labels.milestone || "Next milestone"} v={nr ? money(nr.target - rep.ytd) : "—"} s={nr ? `to ${compact(nr.target)} → +${money(nr.bonus)}` : "all unlocked"} />
    );
  }
  // A rep's own personal-best is theirs to see — an admin never sees this
  // chip, even while viewing a specific player's earnings.
  if (!hide.best && !isAdmin) {
    tiles.push(<Stat key="best" k={labels.best || "Personal best"} v={best.amount ? compact(best.amount) : "—"} s={best.amount ? (best.label || payName) : `no ${payName} yet`} />);
  }
  if (teamTop && !hide.top) {
    tiles.push(<Stat key="top" k={labels.top || "Team top"} v={teamTop.amount ? compact(teamTop.amount) : "—"} s={(teamTop.name || "").split(" ")[0]} />);
  }

  // Year to date: only when the board filter IS calendar YTD. On "This month"
  // / quarter / week the server already narrowed amounts to the window —
  // running every merged pay rule against that slice with period "year"
  // printed nonsense like $23M.
  const ytdEarned = !hide.ytd && (win === "ytd" || win === "year")
    ? earningsBreakdown({
        program,
        amounts: (rep && rep.amounts) || {},
        kpiLabel: (id) => kpiName(config, id),
        period: "year",
        bankedThisPeriod: ladderState(program, rep?.ytd || 0).banked,
      }).total
    : null;
  const ytdRow = ytdEarned != null ? (
    <SoftBox sx={{ flex: "0 0 auto", display: "flex", justifyContent: "space-between", alignItems: "baseline", mt: "6px", padding: "4px 12px 1px" }}>
      <span style={{ fontSize: "11.5px", fontWeight: 500, color: C.muted }}>{labels.ytd || "YTD total earned"}</span>
      <span style={{ fontSize: "13.5px", fontWeight: 600, color: C.ink, fontVariantNumeric: "tabular-nums" }}>{money(ytdEarned)}</span>
    </SoftBox>
  ) : null;

  /* Three scenes. Same numbers, same reading order, same total — only the shape
     changes, so switching is a look and never a different card.
       classic — the original: number, three chips across, the rules beneath.
                 Stays the DEFAULT, so no existing board moves on its own.
       ledger  — split: the money in an inset tinted panel, sums on white
       rows    — split: the sums in the tinted panel, each rule on a white row */
  if (scene === "classic") {
    return (
      <Shell header={<Head picker={picker} title={title} />}>
        <SoftBox sx={{ flex: "0 0 auto", mt: "8px" }}>
          {/* HERO FIGURE tier — 22/600/-0.02em, the same as a Beat the Yeti tile
              value and the milestone total. One size for "the number this card
              exists for", wherever it appears on the board. */}
          <span style={{ fontSize: "22px", fontWeight: 600, letterSpacing: "-0.02em", color: C.ink, fontVariantNumeric: "tabular-nums" }}>{money(total)}</span>
          {!hide.trend && <Delta pct={repDelta} />}
          {/* META tier — 11/500 */}
          <div style={{ fontSize: "11px", fontWeight: 500, color: C.muted, marginTop: "3px" }}>
            {rollupOnly && data?.combinedWorkspaceNames
              ? `Team average across ${data.combinedWorkspaceNames} · commission + bonuses ${periodLabel}`
              : `Commission + bonuses ${periodLabel}`}
          </div>
        </SoftBox>

        {tiles.length > 0 && (
          <SoftBox sx={{ flex: "0 0 auto", display: "grid",
            /* auto-fit, not a fixed column per chip: with three across, opening
               the customize drawer squeezed each one to ~100px and truncated
               every label ("Next miles…"). Below the min they drop to a second
               row instead of clipping. */
            gridTemplateColumns: "repeat(auto-fit, minmax(112px, 1fr))",
            gap: "9px", mt: "10px" }}>
            {tiles}
          </SoftBox>
        )}

        {/* The arithmetic sits on its own tinted panel — inset, with air around
            it, never bleeding to the card's edges. It gives the working-out a
            quiet frame and leaves the headline as the only thing on plain white,
            so the money reads before the sums. The total is the panel's last
            line rather than a separate strip: it belongs to this story.

            SCROLL, don't clip — an admin can compose any number of rules, and a
            swallowed row reads as "my bonus isn't showing" even though its money
            is still counted in the total. */}
        <SoftBox
          sx={{
            flex: "1 1 auto",
            minHeight: 0,
            mt: "10px",
            background: PANEL,
            borderRadius: "10px",
            padding: "10px 11px 11px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div style={{ ...KLABEL, marginBottom: "7px" }}>How it adds up</div>
          {/* Each rule lifts off the panel as its own white row, and the total
              settles back into the panel as a tinted band. The rows read as the
              things being added; the band reads as the answer. */}
          <SoftBox className="gmbw-earn-rows" sx={{ flex: "1 1 auto", minHeight: 0, overflowY: "auto", overflowX: "hidden" }}>
            {rows.map((r) => (
              <Row
                key={r.key}
                label={r.missing ? `${r.label} — metric removed` : r.label}
                sub={r.detail}
                value={r.informational ? "—" : money(r.value)}
                dim={r.dim || r.missing}
                chip
              />
            ))}
          </SoftBox>
          <SoftBox sx={{ flex: "0 0 auto", display: "flex", justifyContent: "space-between", alignItems: "center", mt: "7px", background: "var(--tvd-tile, #EDF1F6)", borderRadius: "8px", padding: "10px 12px" }}>
            <span style={{ fontSize: "12.5px", fontWeight: 500, color: C.body || "var(--tvd-ink, #344054)" }}>Total</span>
            <span style={{ fontSize: "19px", fontWeight: 600, letterSpacing: "-0.02em", color: C.ink, fontVariantNumeric: "tabular-nums" }}>{money(total)}</span>
          </SoftBox>
          {ytdRow}
        </SoftBox>
      </Shell>
    );
  }

  const rowsScene = scene === "rows";

  return (
    <Shell header={<Head picker={picker} title={title} />} bodyPad="8px">
      <SoftBox sx={{ display: "grid", gridTemplateColumns: { mini: "1fr", sm: "192px 1fr" }, gap: "8px", minHeight: 0 }}>
        {/* the money */}
        <SoftBox sx={{ padding: "12px 12px 14px", borderRadius: "10px", minWidth: 0, background: rowsScene ? "transparent" : PANEL }}>
          <div style={KLABEL}>Total earnings</div>
          <div style={{ fontSize: "34px", fontWeight: 600, letterSpacing: "-0.03em", lineHeight: 1, color: C.ink, marginTop: "7px", fontVariantNumeric: "tabular-nums" }}>
            {money(total)}
            {!hide.trend && <Delta pct={repDelta} />}
          </div>
          <div style={{ fontSize: "11.5px", color: C.muted, marginTop: "7px" }}>Commission + bonuses</div>
          {tiles.length > 0 && (
            <SoftBox sx={{ mt: "15px", pt: "12px", borderTop: `1px solid ${C.line}`, display: "flex", flexDirection: "column", gap: "9px" }}>
              {tiles}
            </SoftBox>
          )}
        </SoftBox>

        {/* the working-out. SCROLL, never clip: the card is grid-sized, but an
            admin can compose any number of rules — a swallowed row reads as
            "my bonus isn't showing" even though its money is in the total. */}
        <SoftBox
          className="gmbw-earn-rows"
          sx={{ padding: "12px 12px 14px", borderRadius: "10px", minWidth: 0, minHeight: 0, overflowY: "auto", overflowX: "hidden", background: rowsScene ? PANEL : "transparent" }}
        >
          <div style={{ ...KLABEL, marginBottom: "7px" }}>How it adds up</div>
          {rows.map((r) => (
            <Row
              key={r.key}
              label={r.missing ? `${r.label} — metric removed` : r.label}
              sub={r.detail}
              value={r.informational ? "—" : money(r.value)}
              dim={r.dim || r.missing}
              chip={rowsScene}
            />
          ))}
          <SoftBox
            sx={rowsScene
              ? { display: "flex", justifyContent: "space-between", alignItems: "baseline", mt: "9px", background: "var(--tvd-tile, #EEF2F7)", borderRadius: "8px", padding: "11px 12px" }
              : { display: "flex", justifyContent: "space-between", alignItems: "baseline", mt: "11px", pt: "11px", borderTop: `1.5px solid ${C.ink}` }}
          >
            <span style={{ fontSize: "12px", fontWeight: 500, color: C.body || "var(--tvd-ink, #344054)" }}>Total</span>
            <span style={{ fontSize: "19px", fontWeight: 600, letterSpacing: "-0.02em", color: C.ink, fontVariantNumeric: "tabular-nums" }}>{money(total)}</span>
          </SoftBox>
          {ytdRow}
        </SoftBox>
      </SoftBox>
    </Shell>
  );
} 
